import { useEffect, useState, type FormEvent } from 'react';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import type { TeamInvitePreview } from '../store/teams';

interface JoinTeamDialogProps {
  open: boolean;
  isLookingUp: boolean;
  isJoining: boolean;
  errorMessage: string | null;
  onCancel: () => void;
  onLookupInvite: (inviteCode: string) => Promise<TeamInvitePreview | null>;
  onConfirmJoin: (inviteCode: string) => Promise<boolean>;
}

export default function JoinTeamDialog({
  open,
  isLookingUp,
  isJoining,
  errorMessage,
  onCancel,
  onLookupInvite,
  onConfirmJoin,
}: Readonly<JoinTeamDialogProps>): JSX.Element | null {
  const [inviteCode, setInviteCode] = useState('');
  const [invitePreview, setInvitePreview] = useState<TeamInvitePreview | null>(null);

  useEffect(() => {
    if (open) {
      setInviteCode('');
      setInvitePreview(null);
    }
  }, [open]);

  if (!open) {
    return null;
  }

  const isPending = isLookingUp || isJoining;
  const trimmedInviteCode = inviteCode.trim();

  const handleCancel = () => {
    if (!isPending) {
      onCancel();
    }
  };

  const handleLookup = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!trimmedInviteCode || isPending) {
      return;
    }

    const preview = await onLookupInvite(trimmedInviteCode);
    if (preview) {
      setInvitePreview(preview);
    }
  };

  const handleConfirmJoin = async () => {
    if (!invitePreview || isPending) {
      return;
    }

    await onConfirmJoin(invitePreview.inviteCode);
  };

  return (
    <Dialog open>
      <DialogOverlay onClick={handleCancel} />
      <DialogContent role="dialog" aria-modal="true" aria-label="Join Team">
        {invitePreview ? (
          <div className="flex flex-col gap-3">
            <DialogHeader>
              <DialogTitle>Join Team</DialogTitle>
              <DialogDescription>{`Join "${invitePreview.teamName}"?`}</DialogDescription>
            </DialogHeader>
            {errorMessage ? (
              <p className="m-0 text-xs text-destructive">{errorMessage}</p>
            ) : null}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCancel} disabled={isPending}>
                Cancel
              </Button>
              <Button type="button" onClick={() => { void handleConfirmJoin(); }} disabled={isPending}>
                {isJoining ? 'Confirm...' : 'Confirm'}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form className="flex flex-col gap-3" onSubmit={handleLookup}>
            <DialogHeader>
              <DialogTitle>Join Team</DialogTitle>
              <DialogDescription>
                Enter the invite code shared with you by a team member.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-[0.35rem]">
              <Label htmlFor="join-team-invite-code">Invite code</Label>
              <Input
                id="join-team-invite-code"
                value={inviteCode}
                placeholder="Paste invite code"
                disabled={isPending}
                onChange={(event) => setInviteCode(event.target.value)}
                autoFocus
              />
              {errorMessage ? (
                <p className="m-0 text-xs text-destructive">{errorMessage}</p>
              ) : null}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCancel} disabled={isPending}>
                Cancel
              </Button>
              <Button type="submit" disabled={!trimmedInviteCode || isPending}>
                {isLookingUp ? 'Join...' : 'Join'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
